import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Phone, Users, DollarSign, TrendingDown } from 'lucide-react';
import { usePartnerAnalytics } from '@/hooks/usePartnerAnalytics';
import { usePartnerCommissions } from '@/hooks/usePartnerCommissions';
import { Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { Bar, BarChart, XAxis, YAxis, LabelList } from 'recharts';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import Onboarding from './dashboard/Onboarding';
import { usePartners } from '@/hooks/usePartners';
interface PartnerOverviewProps {
  partner: any;
}

function PartnerOverview({ partner }: PartnerOverviewProps) {
  const { data: analytics, isLoading: analyticsLoading, error: analyticsError } = usePartnerAnalytics();
  const { data: commissionsData, isLoading: commissionsLoading, error: commissionsError } = usePartnerCommissions();
  const { data: partnerDetails } = usePartners() 
  const partnerType = partner.user_type || partner.partner_type;
  const isWhiteLabel = partnerType === 'white_label';
  const onboardingCompleted = partnerDetails?.onboarding_completed;

  // Get colors for charts
  const primaryColor = isWhiteLabel ? partner.branding_settings?.colors?.primary : "hsl(var(--primary))";

  // Calculate metrics from real data
  const metrics = useMemo(() => {
    const commissions = commissionsData?.commissions || [];
    const summary = commissionsData?.summary || {
      total_commission: 0,
      total_revenue: 0,
      pending_commission: 0,
      commission_count: 0
    };

    // Calculate growth rate from commission history
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    
    const currentMonthCommission = commissions.find(c => 
      new Date(c.period_start) >= currentMonth
    )?.commission_amount || 0;
    
    const previousMonthCommission = commissions.find(c => 
      new Date(c.period_start) >= previousMonth && new Date(c.period_start) < currentMonth
    )?.commission_amount || 0;

    const growthRate = previousMonthCommission > 0 
      ? ((currentMonthCommission - previousMonthCommission) / previousMonthCommission * 100) 
      : currentMonthCommission > 0 ? 100 : 0;

    return {
      totalSubAccounts: analytics?.users.total || 0,
      activeUsers: analytics?.users.active || 0,
      growthRate: Math.round(growthRate * 10) / 10, // Round to 1 decimal
      monthlyRevenue: analytics?.revenue.total || 0,
      totalCalls: analytics?.usage.totalCalls || 0,
      totalMinutes: analytics?.usage.totalMinutes || 0,
      avgCallsPerUser: analytics?.users?.total && analytics?.users.total > 0 
        ? Math.round((analytics?.usage?.totalCalls || 0) / analytics.users.total) 
        : 0,
      totalCommissionEarned: summary.total_commission,
      pendingCommission: summary.pending_commission,
      commissionAmount: analytics?.revenue.commissionAmount || currentMonthCommission,
      conversionRate: analytics?.users?.total && analytics.users.total > 0 
        ? Math.round((analytics?.users.active || 0) / analytics.users.total * 100)
        : 0
    };
  }, [analytics, commissionsData]);


  // Chart configuration for Shadcn charts - using your brand colors
  const chartConfig = {
    revenue: {
      label: "Revenue",
      color: primaryColor,
    },
    commission: {
      label: "Commission",
      color: primaryColor,
    },
  } satisfies ChartConfig;

  // Generate 12 months of chart data from real commission records
  const chartData = useMemo(() => {
    const months = [];
    const now = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en', { month: 'long' });
      
      // Find commission for this month
      const monthCommission = commissionsData?.commissions?.find(c => {
        const commissionDate = new Date(c.period_start);
        return commissionDate.getMonth() === date.getMonth() && 
               commissionDate.getFullYear() === date.getFullYear();
      });

      months.push({
        month: monthName,
        revenue: monthCommission?.total_revenue || 0,
        commission: monthCommission?.commission_amount || 0,
      });
    }
    
    return months;
  }, [commissionsData]);

  // Calculate responsive chart maximum for better scaling (handles $0 to millions)
  const chartMaxValue = useMemo(() => {
    const maxDataValue = Math.max(...chartData.map(d => isWhiteLabel ? d.revenue : d.commission));

    if (maxDataValue === 0) return 1000; // Default minimum scale

    // Dynamic scaling based on value magnitude
    if (maxDataValue >= 1000000) {
      // Millions: round to nearest 250K, add 25% padding
      return Math.ceil(maxDataValue * 1.25 / 250000) * 250000;
    } else if (maxDataValue >= 100000) {
      // Hundreds of thousands: round to nearest 25K, add 30% padding
      return Math.ceil(maxDataValue * 1.3 / 25000) * 25000;
    } else if (maxDataValue >= 10000) {
      // Ten thousands: round to nearest 2.5K, add 40% padding
      return Math.ceil(maxDataValue * 1.4 / 2500) * 2500;
    } else if (maxDataValue >= 1000) {
      // Thousands: round to nearest 500, add 50% padding
      return Math.ceil(maxDataValue * 1.5 / 500) * 500;
    } else {
      // Under $1000: ensure minimum $1000 scale for better visual proportion
      return 1000;
    }
  }, [chartData, isWhiteLabel]);

  // Loading state
  if (analyticsLoading || commissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading dashboard...</span>
      </div>
    );
  }

  // Error state
  if (analyticsError || commissionsError) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-red-500">
        <div className="text-center space-y-2">
          <p>Error loading dashboard data</p>
          {analyticsError && <p className="text-sm">Analytics: {analyticsError.message}</p>}
          {commissionsError && <p className="text-sm">Commissions: {commissionsError.message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* KPI Cards */}
      {onboardingCompleted && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium">Total Sub-Accounts</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.activeUsers} active this month
            </p>
          </CardHeader>
          <CardFooter className="mt-auto pt-2 pb-2">
            <div className="text-2xl font-bold">{metrics.totalSubAccounts}</div>
          </CardFooter>
        </Card>

        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium">
                {isWhiteLabel ? 'Monthly Revenue' : 'Commission Earned'}
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </div>
            <p className="text-xs text-muted-foreground mt-1 flex items-center">
              {metrics.growthRate >= 0 ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1" />
              )}
              {metrics.growthRate >= 0 ? '+' : ''}{metrics.growthRate}% from last month
            </p>
          </CardHeader>
          <CardFooter className="mt-auto pt-2 pb-2">
            <div className="text-2xl font-bold">
              ${(isWhiteLabel ? metrics.monthlyRevenue : metrics.commissionAmount).toFixed(2)}
            </div>
          </CardFooter>
        </Card>

        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium">Total Calls</CardTitle>
              <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.avgCallsPerUser} avg per user
            </p>
          </CardHeader>
          <CardFooter className="mt-auto pt-2 pb-2">
            <div className="text-2xl font-bold">{metrics.totalCalls}</div>
          </CardFooter>
        </Card>

        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <div className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium">
                {isWhiteLabel ? 'Active Rate' : 'Total Earned'}
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isWhiteLabel ? 'Active user rate' : `$${metrics.pendingCommission.toFixed(2)} pending`}
            </p>
          </CardHeader>
          <CardFooter className="mt-auto pt-2 pb-2">
            <div className="text-2xl font-bold">
              {isWhiteLabel ? `${metrics.conversionRate}%` : `$${metrics.totalCommissionEarned.toFixed(2)}`}
            </div>
          </CardFooter>
        </Card>
      </div>}

      {/* Revenue Chart - Shadcn Horizontal Bar Chart */}
      {onboardingCompleted && <Card>
        <CardHeader>
          <CardTitle>Revenue Trend (Last 12 Months)</CardTitle>
          <CardDescription>January - December 2024</CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-[400px]">
            <BarChart
              accessibilityLayer
              data={chartData}
              layout="vertical"
              margin={{
                left: -20,
              }}
            >
              <XAxis
                type="number"
                dataKey={isWhiteLabel ? "revenue" : "commission"}
                domain={[0, chartMaxValue]}
                hide
              />
              <YAxis
                dataKey="month"
                type="category"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) => value.slice(0, 3)}
              />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              <Bar 
                dataKey={isWhiteLabel ? "revenue" : "commission"} 
                fill={`var(--color-${isWhiteLabel ? "revenue" : "commission"})`} 
                radius={5} 
              >
                <LabelList
                  position="right"
                  offset={12}
                  className="fill-foreground"
                  fontSize={12}
                  formatter={(value: number) => `$${value.toLocaleString()}`}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
        <CardFooter className="flex-col items-start gap-2 text-sm">
          <div className="flex gap-2 leading-none font-medium">
            Trending up by {metrics.growthRate >= 0 ? '+' : ''}{metrics.growthRate}% this month
            {metrics.growthRate >= 0 ? (
              <TrendingUp className="h-4 w-4" />
            ) : (
              <TrendingDown className="h-4 w-4" />
            )}
          </div>
        </CardFooter>
      </Card>}
      <Onboarding isWhiteLabel={isWhiteLabel} />
    </div>
  );
}

export default PartnerOverview;